import React, { useEffect, useMemo, useState } from "react";
import { MdLanguage } from "react-icons/md";
import { isAuthenticated, SetElakayLang } from "../../auth";
import { updateMyProfileAPI } from "../../auth/apiUserProfile";
import { elakayAutoText, getElakayLanguageCode } from "../../pageInside/dataElakay";
import { supportedLanguageOptions } from "../../pageInside/LanguageSelector";
import { SUPPORTED_USER_LANGUAGES, resolveUserLanguageCode } from "../../pageInside/userLanguages";
import { profileErrorMessage } from "./profileErrors";
import "./profileIdentity.css";

// My Profile -> Default Language: ONE language per account, stored in the
// existing users.language object ({ code, name, nativeName }, built by the
// backend from the code). This is the value Email Marketing's "Spoken
// Language" filter reads. Saving also switches the interface to that
// language through the regular header mechanism (SetElakayLang), so the
// page and the saved value never disagree right after a change.
const DefaultLanguageSection = ({ infoSource }) => {
  const storedCode = resolveUserLanguageCode(infoSource?.userInfo?.language);
  // No saved (or an unsupported legacy) language: preselect the language
  // the interface is currently shown in -- nothing is written until Save.
  const initialCode = storedCode || getElakayLanguageCode();
  const [selected, setSelected] = useState(initialCode);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { setSelected(storedCode || getElakayLanguageCode()); }, [storedCode]);

  const translatedNames = useMemo(
    () => new Map(supportedLanguageOptions(infoSource).map((option) => [option.value, option.label])),
    [infoSource]
  );

  const save = async (event) => {
    event.preventDefault();
    if (saving || !selected) return;
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const result = await updateMyProfileAPI({ languageCode: selected });
      await infoSource?.readUser?.(isAuthenticated()?.token, ["getUserInfo"]);
      if (result?.user?.language?.code) SetElakayLang(result.user.language);
      setMessage(elakayAutoText("h_prf_languageUpdated", "Language Updated Successfully"));
    } catch (e) {
      setError(profileErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  // Wait for the account (readMe) instead of flashing "not saved yet".
  if (!infoSource?.userInfo?._id) return null;

  // Compact dropdown rendered inside the My Profile identity card, beside
  // the default address (see ComponentRegistry.jsx's UserInfoCard).
  return (
    <section className="prf-lang" aria-labelledby="prf-default-language-title">
      <form className="prf-lang-form" onSubmit={save}>
        <label id="prf-default-language-title" htmlFor="prf-default-language" className="prf-lang-label">
          <MdLanguage aria-hidden="true" className="prf-lang-icon" />
          {elakayAutoText("h_prf_defaultLanguage_title", "Default Language")}
        </label>
        <div className="prf-lang-control">
          <select
            id="prf-default-language"
            className="prf-lang-select"
            value={selected}
            disabled={saving}
            aria-describedby="prf-default-language-desc"
            onChange={(event) => { setSelected(event.target.value); setMessage(""); setError(""); }}
          >
            {SUPPORTED_USER_LANGUAGES.map((language) => {
              const translated = translatedNames.get(language.code) || language.name;
              return (
                <option key={language.code} value={language.code} lang={language.code}>
                  {translated !== language.nativeName ? `${language.nativeName} — ${translated}` : language.nativeName}
                </option>
              );
            })}
          </select>
          <button type="submit" className="prf-lang-save" disabled={saving || (selected === storedCode)}>
            {saving ? elakayAutoText("h_prf_saving", "Saving…") : elakayAutoText("h_prf_saveChanges", "Save Changes")}
          </button>
        </div>
        <p id="prf-default-language-desc" className="prf-lang-hint">
          {storedCode
            ? elakayAutoText("h_prf_defaultLanguage_desc", "The language Elakay uses for you, including the marketing emails you receive.")
            : elakayAutoText("h_prf_languageNotSaved", "No default language saved yet. The current interface language is preselected.")}
        </p>
      </form>
      {error && <div className="epm-alert-danger prf-lang-feedback" role="alert">{error}</div>}
      {message && <div className="epm-banner prf-success prf-lang-feedback" role="status">{message}</div>}
    </section>
  );
};

export default DefaultLanguageSection;
